import { Box, Button, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import { format, isThisISOWeek, isToday } from "date-fns";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  FilterSearchField,
  SearchFilter,
} from "../../common/components/functional/FilterSearchField/FilterSearchField";
import { GroupedTable } from "../../common/components/functional/GroupedTable/GroupedTable";
import OverlaySpinner from "../../common/components/functional/overlaySpinner/OverlaySpinner";
import { Heading1 } from "../../common/components/styled/styled";
import { leadingZerosDateFormat } from "../../common/helpers/formatDateTime";
import formatNumber from "../../common/helpers/formatNumber";
import { ReactComponent as DefaultStatusIcon } from "../../common/icons/attachmentDark.svg";
import { ReactComponent as DraftIcon } from "../../common/icons/duplicate.svg";
import { ReactComponent as EditIcon } from "../../common/icons/editDark.svg";
import { ReactComponent as ErrorIcon } from "../../common/icons/errorRed.svg";
import { ReactComponent as InvoiceSecondWaveIcon } from "../../common/icons/invoiceSecondWave.svg";
import { ReactComponent as SentSuccessIcon } from "../../common/icons/mail.svg";
import { canExpertModifyInvoices } from "../user/accessRightChecks";
import {
  CREDIT_INVOICE_COLOR,
  INITIAL_INVOICE_FILTERS,
  INVOICE_COLUMNS,
  INVOICE_DRAFT,
  INVOICE_EDITED,
  INVOICE_SENT_FAILED,
  INVOICE_SENT_SUCCESS,
  INVOICE_WAVE_TWO,
} from "./constants";
import { NewCreditInvoiceDialog } from "./creditInvoice/NewCreditInvoiceDialog";
import { InvoiceFilters } from "./filters/InvoiceFilters";
import { InvoiceActionBanner } from "./InvoiceActionBanner";
import { InvoiceDisplay, ItemGroup } from "./invoiceAPI";
import { fetchInvoices, selectAll } from "./invoiceSlice";
import { NewInvoiceDialog } from "./newInvoice/NewInvoiceDialog";
import { BottomSpaceForInvoiceActionBanner } from "./styled";
import {
  getInvoiceOriginCode,
  isInvoicePartOfCreditInvoiceBundle,
} from "./utils";

export const InvoicesPage = () => {
  const dispatch = useAppDispatch();

  const translationNs = "invoices";
  const { t } = useTranslation(translationNs);

  const invoices: InvoiceDisplay[] = useAppSelector((state) =>
    selectAll(state),
  );
  const { status } = useAppSelector((state) => state.invoices);
  const isLoading = status === "pending";
  const user = useAppSelector((state) => state.user.user);

  const [isUserAllowedToEdit, setIsUserAllowedToEdit] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState<boolean>(false);
  const [isCreditInvoiceDialogOpen, setIsCreditInvoiceDialogOpen] =
    useState<boolean>(false);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>("");
  const [selectedInvoices, setSelectedInvoices] = useState<InvoiceDisplay[]>(
    [],
  );
  const [createCreditInvoiceId, setCreateCreditInvoiceId] =
    useState<string>("");

  const [invoiceGroups, setInvoiceGroups] = useState<
    ItemGroup<InvoiceDisplay>[]
  >([]);

  const INVOICE_FILTERS_LOCAL_STORAGE = "invoiceFilters";
  const fromLocalStorage = localStorage.getItem(INVOICE_FILTERS_LOCAL_STORAGE);
  const userFilters: SearchFilter[] =
    fromLocalStorage && JSON.parse(fromLocalStorage);

  const sanitisedFilters = userFilters?.map((filter) => {
    // eezyCompanyNames was previously a string, now it should be an array
    if (filter.name === "eezyCompanyNames" && !Array.isArray(filter.value)) {
      return {
        ...filter,
        value: filter.value ? [filter.value] : [],
      };
    }
    return filter;
  });
  const [filters, setFilters] = useState<SearchFilter[]>(
    sanitisedFilters || INITIAL_INVOICE_FILTERS,
  );

  const updateFilters = (filters: any) => {
    setFilters(filters);
    localStorage.setItem(
      INVOICE_FILTERS_LOCAL_STORAGE,
      JSON.stringify(filters),
    );
  };

  const openCreditInvoiceDialog = (invoiceId: string) => {
    setCreateCreditInvoiceId(invoiceId);
    setIsCreditInvoiceDialogOpen(true);
  };

  const closeCreditInvoiceDialog = () => {
    setCreateCreditInvoiceId("");
    setIsCreditInvoiceDialogOpen(false);
  };

  const selectPrevTerm = (term: string) => {
    setSearchString(term);
  };

  const dispatchSearch = useCallback(() => {
    const filterObject = filters.reduce((acc, filter) => {
      const { name, value } = filter;
      return { ...acc, [name]: value };
    }, {});
    dispatch(fetchInvoices({ search: searchString, filters: filterObject }));
  }, [dispatch, filters, searchString]);

  const getStatus = useCallback(
    (invoiceStatus: string, invoiceWave: string) => {
      const DEFAULT_STYLES = {
        minWidth: 16,
        minHeight: 16,
        marginRight: "0.5em",
      };
      let statusIcon = <></>;
      let waveInfo = <></>;
      switch (invoiceStatus) {
        case INVOICE_DRAFT:
          statusIcon = <DraftIcon style={{ ...DEFAULT_STYLES }} />;
          break;
        case INVOICE_EDITED:
          statusIcon = <EditIcon style={{ ...DEFAULT_STYLES }} />;
          break;
        case INVOICE_SENT_SUCCESS:
          statusIcon = <SentSuccessIcon style={{ ...DEFAULT_STYLES }} />;
          break;
        case INVOICE_SENT_FAILED:
          statusIcon = <ErrorIcon style={{ ...DEFAULT_STYLES }} />;
          break;
        default:
          statusIcon = <DefaultStatusIcon style={{ ...DEFAULT_STYLES }} />;
          break;
      }
      switch (invoiceWave) {
        case INVOICE_WAVE_TWO:
          waveInfo = (
            <>
              <InvoiceSecondWaveIcon style={{ ...DEFAULT_STYLES }} />
              <Typography fontSize={14}>
                {t(`invoices:${invoiceWave}`)}
              </Typography>
            </>
          );
          break;
        default:
          break;
      }
      return (
        <Box width="100%">
          <Box display="flex" alignItems="center">
            {statusIcon}
            <Typography fontSize={14}>
              {t(`invoices:${invoiceStatus}`)}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            {waveInfo}
          </Box>
        </Box>
      );
    },
    [t],
  );

  const getOriginInfo = useCallback(
    (invoice: InvoiceDisplay) => {
      const originCode = getInvoiceOriginCode(invoice);
      const originalInvoiceNumber = invoice.originalInvoiceNumber;
      return (
        <>
          <Typography fontSize={14}>{t(originCode)}</Typography>
          <Typography fontSize={14}>{originalInvoiceNumber}</Typography>
        </>
      );
    },
    [t],
  );

  const formatInvoice = useCallback(
    (invoice: InvoiceDisplay, invoiceDateObject: Date) => {
      return {
        ...invoice,
        invoiceDate: format(invoiceDateObject, leadingZerosDateFormat),
        invoiceDueDate: invoice.invoiceDueDate
          ? format(new Date(invoice.invoiceDueDate), leadingZerosDateFormat)
          : "",
        invoiceValueDate: format(
          new Date(invoice.invoiceValueDate),
          leadingZerosDateFormat,
        ),
        vatExcludedAmount: formatNumber(invoice.vatExcludedAmount),
        vatAmount: formatNumber(invoice.vatAmount),
        vatIncludedAmount: formatNumber(invoice.vatIncludedAmount),
        originInfo: getOriginInfo(invoice),
        invoiceStatusTranslation: t(invoice.invoiceStatus),
        flairComponent: getStatus(invoice.invoiceStatus, invoice.invoiceWave),
        customRowColor: isInvoicePartOfCreditInvoiceBundle(invoice)
          ? CREDIT_INVOICE_COLOR
          : undefined,
      };
    },
    [t, getStatus, getOriginInfo],
  );

  const getInvoice = (item: InvoiceDisplay[]): InvoiceDisplay => {
    if (item.length === 1) return item[0];

    const foundBundleInvoice = item.find(
      (invoice) => invoice.originalInvoiceNumber,
    );

    if (!!foundBundleInvoice) return foundBundleInvoice;

    return item[0];
  };

  const constructInvoiceGroups = useCallback(
    (invoices: InvoiceDisplay[]) => {
      // Sort invoices from newest to oldest before grouping
      invoices.sort((a, b) => {
        const dateA = a.invoiceDate ? new Date(a.invoiceDate) : new Date();
        const dateB = b.invoiceDate ? new Date(b.invoiceDate) : new Date();
        return dateB.getTime() - dateA.getTime();
      });

      // Collect invoices into an array of arrays for bundling
      const bundledInvoices = invoices.reduce(
        (objectsByKeyValue: Array<InvoiceDisplay[]>, obj: InvoiceDisplay) => {
          // If invoice has bundleId, check if it can be added to an existing bundle.
          // If invoice doesn't have bundleId or there is no pre-existing bundle,
          // add the invoice to the array on its own.
          const value = obj["bundleId"];
          if (value) {
            const foundBundle = objectsByKeyValue.find(
              (bundle) => bundle[0].bundleId === value,
            );
            if (foundBundle) {
              foundBundle.push(obj);
            } else {
              objectsByKeyValue.push([obj]);
            }
          } else {
            objectsByKeyValue.push([obj]);
          }
          return objectsByKeyValue;
        },
        [],
      );

      // Inside a bundle, sort invoices by number in descending order
      bundledInvoices.forEach((value) => {
        if (value.length > 1) {
          value.sort((a, b) => {
            return Number(b.invoiceNumber) - Number(a.invoiceNumber);
          });
        }
      });

      let groups: ItemGroup<InvoiceDisplay>[] = [];
      // Item is an array of invoices (single invoices are an array of one)
      bundledInvoices.forEach((item) => {
        // Define group for the bundle based on one of the new invoices.
        // If we have only one invoice in the bundle, use it instead.
        // For unbundled invoices, define group based on invoice date.
        const invoice = getInvoice(item);

        const invoiceDateObject = !!invoice.invoiceDate
          ? new Date(invoice.invoiceDate)
          : new Date();
        const invoiceYear = invoiceDateObject.getFullYear();
        let groupName: string;

        if (isToday(invoiceDateObject)) {
          // Today
          groupName = "today";
        } else if (isThisISOWeek(invoiceDateObject)) {
          // This calendar week
          groupName = "thisWeek";
        } else {
          // Current and/or previous months
          groupName = `${invoiceYear}-${invoiceDateObject.getMonth() + 1}`;
        }

        const formattedInvoiceData: InvoiceDisplay | InvoiceDisplay[] =
          item.length > 1
            ? item.map((invoice) => {
                let dateObject = !!invoice.invoiceDate
                  ? new Date(invoice.invoiceDate)
                  : new Date();
                return formatInvoice(invoice, dateObject);
              })
            : formatInvoice(invoice, invoiceDateObject);

        const invoiceGroup = groups.find((groups) => groups.name === groupName);
        if (!invoiceGroup) {
          groups.push({
            name: groupName,
            year:
              invoiceYear < new Date().getFullYear()
                ? invoiceYear.toString()
                : undefined,
            items: [formattedInvoiceData],
          });
        } else {
          invoiceGroup.items.push(formattedInvoiceData);
        }
      });
      setInvoiceGroups(groups);
    },
    [formatInvoice],
  );

  const handleFilterMenuOpenClose = () => {
    setIsFilterMenuOpen(!isFilterMenuOpen);
  };

  const customSelectHandler = (
    item: any,
    selected: boolean,
    selectedItems: any[],
  ) => {
    // Custom logic if item is credit invoice item
    if (isInvoicePartOfCreditInvoiceBundle(item)) {
      // Find the Credit invoice pair
      const creditItemPair = invoices.filter(
        (invoice) =>
          invoice.originalInvoiceNumber === item.originalInvoiceNumber &&
          invoice.bundleId === item.bundleId,
      );

      if (selected) {
        // If selected is true, add both items to selectedItems
        return [...selectedItems, ...creditItemPair];
      } else {
        // If selected is false, remove both items from selectedItems
        const updatedSelectedItems = selectedItems.filter(
          (selectedItem) =>
            !creditItemPair.some((item) => item.id === selectedItem.id),
        );
        return updatedSelectedItems;
      }
    }

    // Normal case, default behaviour
    if (selected) {
      // Add item
      return [...selectedItems, item];
    } else {
      // Remove item
      return selectedItems.filter(
        (selectedItem) => selectedItem.id !== item.id,
      );
    }
  };

  const updateSelectedItems = (selectedItems: InvoiceDisplay[]) => {
    setSelectedInvoices(selectedItems);
  };

  const buildInvoiceUrl = (invoice: InvoiceDisplay): string => {
    const invoicesToBeOpened = [invoice.invoiceNumber];

    if (isInvoicePartOfCreditInvoiceBundle(invoice)) {
      // Find the other Credit invoice items
      const foundCreditInvoiceBundleItems = invoices.filter(
        (item) =>
          item.originalInvoiceNumber === invoice.originalInvoiceNumber &&
          item.id !== invoice.id,
      );

      // If foundCreditInvoiceBundleItems, add them to invoicesToBeOpened
      if (foundCreditInvoiceBundleItems.length > 0) {
        foundCreditInvoiceBundleItems.forEach((item) => {
          invoicesToBeOpened.push(item.invoiceNumber);
        });
      }
    }

    return `/home/invoices/preview/${invoicesToBeOpened.join(",")}`;
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      dispatchSearch();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [filters, searchString, dispatchSearch]);

  useEffect(() => {
    constructInvoiceGroups(invoices);
  }, [invoices, t, constructInvoiceGroups]);

  useEffect(() => {
    const selectedInvoiceIds = selectedInvoices.map((invoice) => invoice.id);
    const updatedSelectedInvoices: InvoiceDisplay[] = invoices.filter(
      (invoice) => selectedInvoiceIds.includes(invoice.id),
    );
    setSelectedInvoices(updatedSelectedInvoices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);

  useEffect(() => {
    if (!user) return;
    setIsUserAllowedToEdit(canExpertModifyInvoices(user));
  }, [user]);

  return (
    <Box data-testid="invoices-page-root">
      <OverlaySpinner isLoading={isLoading} />
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Heading1 variant="header">{t("invoices")}</Heading1>
        {isUserAllowedToEdit ? (
          <Button
            variant="contained"
            sx={{ borderRadius: "18px", bgcolor: "primary.light" }}
            startIcon={<AddIcon />}
            onClick={() => setIsAddDialogOpen(true)}
          >
            {t("addNewInvoice")}
          </Button>
        ) : null}
      </Box>
      <FilterSearchField
        width="895px"
        isOpen={isFilterMenuOpen}
        toggleMenu={handleFilterMenuOpenClose}
        label={t("searchFromInvoices") || ""}
        value={searchString}
        onChange={(value) => setSearchString(value)}
        searchHistory={true}
        selectFromHistory={selectPrevTerm}
        translation={translationNs}
        filters={filters}
        updateFilters={updateFilters}
        isLoading={isLoading}
        localStorageName="searchInvoice"
        filtersLocalStorageName={INVOICE_FILTERS_LOCAL_STORAGE}
        defaultFilters={INITIAL_INVOICE_FILTERS}
      >
        <InvoiceFilters
          filters={filters}
          updateFilters={updateFilters}
          handleMenuToggle={handleFilterMenuOpenClose}
        />
      </FilterSearchField>
      <GroupedTable
        columns={INVOICE_COLUMNS}
        groups={invoiceGroups}
        itemCount={invoices.length}
        translation={translationNs}
        updateSelectedItems={updateSelectedItems}
        customSelectHandler={customSelectHandler}
        buildUrl={buildInvoiceUrl}
      />
      <BottomSpaceForInvoiceActionBanner />
      <NewInvoiceDialog
        isOpen={isAddDialogOpen}
        closeDialog={() => setIsAddDialogOpen(false)}
      />
      <NewCreditInvoiceDialog
        isOpen={isCreditInvoiceDialogOpen}
        selectedInvoiceId={createCreditInvoiceId}
        closeDialog={closeCreditInvoiceDialog}
      />
      <InvoiceActionBanner
        translation={translationNs}
        invoices={invoices}
        selectedInvoices={selectedInvoices}
        openCreditInvoiceDialog={openCreditInvoiceDialog}
        isUserAllowedToEdit={isUserAllowedToEdit}
      />
    </Box>
  );
};
